import { BaseShader } from "webgl-framework";
import { cemetery, engine, lib } from "./KotlinLib";
import { SceneRenderer } from "./SceneRenderer";
import { ShaderInputs } from "./shaders/ShaderInputs";
import { DiffuseShader } from "./shaders/DiffuseShader";
import { DepthSimpleShader } from "./shaders/depth/DepthSimpleShader";
import { FlagDepthShader } from "./shaders/depth/FlagDepthShader";
import { DiffuseColoredShader } from "./shaders/DiffuseColoredShader";
import { DiffuseColoredAtShader } from "./shaders/DiffuseColoredAtShader";
import { ColorShader } from "./shaders/ShaderColor";
import { DiffuseColoredLmShader } from "./shaders/DiffuseColoredLmShader";
import { DiffuseDoubleColoredLmShader } from "./shaders/DiffuseDoubleColoredLmShader";
import { TestModelNormalsShader } from "./shaders/TestModelNormalsShader";
import { TestLambertShader } from "./shaders/TestLambertShader";
import { DiffuseHalfLambertAoShader } from "./shaders/DiffuseHalfLambertAoShader";
import { DiffuseHalfLambertAoLmShader } from "./shaders/DiffuseHalfLambertAoLmShader";
import { DiffuseHalfLambertAoLmLitShader } from "./shaders/DiffuseHalfLambertAoLmLitShader";
import { LeavesShader } from "./shaders/LeavesShader";
import { ColorFogShader } from "./shaders/ColorFogShader";
import { GhostShader } from "./shaders/GhostShader";
import { ColorAnimatedTextureChunkedFogShader } from "./shaders/ColorAnimatedTextureChunkedFogShader";
import { mat4, vec3 } from "gl-matrix";

/** Camera position as the `Vec3` of the scene, to paste it into the camera code. */
function formatPosition(position: vec3): string {
    const component = (value: number) => `${value.toFixed(3)}f`;
    return `Vec3(${component(position[0])}, ${component(position[1])}, ${component(position[2])})`;
}

export class Renderer extends SceneRenderer<lib.org.androidworks.cemetery.CemeteryScene> {
    protected SCENE_BOUNDING_BOX = {
        minX: -10000,
        maxX: 10000,
        minY: -10000,
        maxY: 10000,
        minZ: -10000,
        maxZ: 10000
    };
    protected FREE_MOVEMENT_SPEED = 5;

    constructor() {
        super();
        this.initCommands();
    }

    private ssaoPassCommand?: lib.org.androidworks.engine.commands.SsaoPassCommand;

    /**
     * Fills static array `commands` with the commands from the scene. Use only if scene has static commands.
     */
    protected initCommands(): void {
        const walk = (commands: lib.org.androidworks.engine.commands.Command[]) => {
            for (const command of commands) {
                if (command instanceof engine.commands.SsaoPassCommand) {
                    this.ssaoPassCommand = command;
                }
                if (command instanceof engine.commands.GroupCommand) {
                    walk(command.commands.toArray());
                }
            }
        };
        walk(this.scene!.commands.toArray());
    }

    createScene(): lib.org.androidworks.cemetery.CemeteryScene {
        const scene = new cemetery.CemeteryScene();
        scene.randomCamera();
        return scene;
    }

    /** Camera positions saved with the number keys, printed with 5; see [onCameraKeyDown]. */
    private camera1?: vec3;
    private camera2?: vec3;
    private camera3?: vec3;
    private camera4?: vec3;

    /** Scratch of [currentCameraPosition]. */
    private readonly inverseViewMatrix = mat4.create();

    onAfterInit(): void {
        super.onAfterInit();

        document.addEventListener("keydown", this.onCameraKeyDown);
    }

    /**
     * Saves the position of the camera of the current frame on the keys 1 to 4, and puts the
     * saved ones on the clipboard on 5, ready to be pasted into the camera code of the scene.
     */
    private readonly onCameraKeyDown = (event: KeyboardEvent): void => {
        // Not while a value is typed into a field of the GUI: those digits are its own.
        const target = event.target as HTMLElement | null;
        if (target !== null && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
            return;
        }

        if (event.key === "5") {
            this.copySavedCameras();
            return;
        }

        const position = this.currentCameraPosition();
        if (position === undefined) {
            return;
        }

        switch (event.key) {
            case "1": this.camera1 = position; break;
            case "2": this.camera2 = position; break;
            case "3": this.camera3 = position; break;
            case "4": this.camera4 = position; break;
            default: return;
        }

        console.log(`Camera ${event.key}: ${formatPosition(position)}`);
    };

    /**
     * World space position of the camera the current frame is rendered with. It is read from the
     * view matrix of the scene, so it is the camera of whichever camera mode drives it.
     */
    private currentCameraPosition(): vec3 | undefined {
        const scene = this.scene;
        if (scene === undefined) {
            return undefined;
        }

        mat4.invert(this.inverseViewMatrix, scene.matView);

        return vec3.fromValues(
            this.inverseViewMatrix[12],
            this.inverseViewMatrix[13],
            this.inverseViewMatrix[14]
        );
    }

    /** Puts the positions saved with the number keys on the clipboard, one per line. */
    private copySavedCameras(): void {
        const lines = `        CameraPositionPair(
            CameraPosition(
                Vec3(${this.camera1![0].toFixed(5)}f, ${this.camera1![1].toFixed(5)}f, ${this.camera1![2].toFixed(5)}f),
                Vec3(${this.camera2![0].toFixed(5)}f, ${this.camera2![1].toFixed(5)}f, ${this.camera2![2].toFixed(5)}f),
            ),
            CameraPosition(
                Vec3(${this.camera3![0].toFixed(5)}f, ${this.camera3![1].toFixed(5)}f, ${this.camera3![2].toFixed(5)}f),
                Vec3(${this.camera4![0].toFixed(5)}f, ${this.camera4![1].toFixed(5)}f, ${this.camera4![2].toFixed(5)}f)
            ),
            1f
        ),
`;

        // The clipboard is only there in a secure context, and only writes while the page has
        // the focus. A capture must not be lost to that, so the values are logged when it fails.
        const clipboard = navigator.clipboard;
        if (clipboard === undefined) {
            return;
        }

        clipboard.writeText(lines).then(
            () => console.log("Cameras copied to the clipboard"),
            () => {
                console.log("The clipboard is not available, the cameras are:");
            }
        );
    }

    createShader(name: string): (BaseShader & ShaderInputs) {
        // const filters = getFilters("gl_FragColor");
        // const filter = filters[this.scene?.settings.colorMode.name!];

        if (name === "Diffuse") {
            return new DiffuseShader(this.gl);
        } else if (name === "DiffuseHalfLambertAO") {
            return new DiffuseHalfLambertAoShader(this.gl);
        } else if (name === "DiffuseHalfLambertAoLm") {
            return new DiffuseHalfLambertAoLmShader(this.gl);
        } else if (name === "DiffuseHalfLambertAoLmLit") {
            return new DiffuseHalfLambertAoLmLitShader(this.gl);
        } else if (name === "DiffuseColoredAt") {
            return new DiffuseColoredAtShader(this.gl, []);
        } else if (name === "DiffuseColoredLm") {
            return new DiffuseColoredLmShader(this.gl, []);
        } else if (name === "DiffuseDoubleColoredLm") {
            return new DiffuseDoubleColoredLmShader(this.gl, []);
        } else if (name === "DiffuseColored") {
            return new DiffuseColoredShader(this.gl, []);
        } else if (name === "Color") {
            return new ColorShader(this.gl);
        } else if (name === "ColorFog") {
            return new ColorFogShader(this.gl);
        } else if (name === "Ghost") {
            return new GhostShader(this.gl);
        } else if (name === "ColorAnimatedTextureChunkedFog") {
            return new ColorAnimatedTextureChunkedFogShader(this.gl);
        } else if (name === "TestModelNormals") {
            return new TestModelNormalsShader(this.gl);
        } else if (name === "TestLambert") {
            return new TestLambertShader(this.gl);
        } else if (name === "Leaves") {
            return new LeavesShader(this.gl);
        }

        throw new Error(`Unknown shader type ${name}`);
    }

    public get ssaoResolutionScale(): number {
        return this.ssaoPassCommand?.resolutionScale ?? 0.5;
    }

    public set ssaoResolutionScale(value: number) {
        if (this.ssaoPassCommand !== undefined) {
            this.ssaoPassCommand.resolutionScale = value;
        }
    }

    public get ssaoBlurSize(): string {
        return this.ssaoPassCommand?.blurSize.name ?? "KERNEL_3";
    }

    public set ssaoBlurSize(value: string) {
        if (this.ssaoPassCommand !== undefined) {
            // BlurSize is not @JsExport'd, so entries are accessed via valueOf.
            this.ssaoPassCommand.blurSize = (engine.commands.BlurSize as any).valueOf(value);
        }
    }

    public get ssaoRadius(): number {
        return this.ssaoPassCommand?.radius ?? 18;
    }

    public set ssaoRadius(value: number) {
        if (this.ssaoPassCommand !== undefined) {
            this.ssaoPassCommand.radius = value;
        }
    }

    public get ssaoDepthRange(): number {
        return this.ssaoPassCommand?.depthRange ?? 8;
    }

    public set ssaoDepthRange(value: number) {
        if (this.ssaoPassCommand !== undefined) {
            this.ssaoPassCommand.depthRange = value;
        }
    }

    public get ssaoBias(): number {
        return this.ssaoPassCommand?.bias ?? 0;
    }

    public set ssaoBias(value: number) {
        if (this.ssaoPassCommand !== undefined) {
            this.ssaoPassCommand.bias = value;
        }
    }

    public get ssaoIntensity(): number {
        return this.ssaoPassCommand?.intensity ?? 1.1;
    }

    public set ssaoIntensity(value: number) {
        if (this.ssaoPassCommand !== undefined) {
            this.ssaoPassCommand.intensity = value;
        }
    }

    public get settings() {
        return this.scene!.settings;
    }

    nextCamera(): void {
        this.scene?.nextCamera();
    }

    randomCamera(): void {
        this.scene?.randomCamera();
    }

    swipeCameraLeft(): void {
        this.scene?.cameraImpulse(-15);
    }

    swipeCameraRight(): void {
        this.scene?.cameraImpulse(15);
    }

    /** Test visualization: draws the bounding boxes of the imported models as wireframes. */
    public showAabbWireframes = false;

    /** Draw calls of the active render mode, culled by the scene every frame. */
    public get totalDrawCalls(): number {
        return this.scene?.totalDrawCalls ?? 0;
    }

    /** How many of those draw calls the scene culled as outside the view frustum. */
    public get culledDrawCalls(): number {
        return this.scene?.culledDrawCalls ?? 0;
    }

    drawScene(): void {
        this.applySsaoQuality();
        super.drawScene();

        if (this.showAabbWireframes) {
            this.drawSceneAabbWireframes();
        }
    }

    /**
     * Hands the "Low quality" switch of the scene over to the SSAO pass, which then draws the
     * AO with its cheap shader. Runs before the pass of this frame, so a change made in the UI
     * takes effect immediately.
     */
    private applySsaoQuality(): void {
        if (this.ssaoPass !== undefined) {
            this.ssaoPass.lowQuality = this.scene?.settings.lowQuality ?? false;
        }
    }

    /** Wireframes of the bounding boxes of all draw calls, see createDrawCallsForMesh. */
    private drawSceneAabbWireframes(): void {
        if (this.scene === undefined) {
            return;
        }

        for (const bounds of this.scene.drawCallBounds.toArray()) {
            this.drawAabbWireframe(bounds);
        }
    }
}
